import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef } from 'react';
import { useOutletContext, useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, Inbox, GanttChartSquare, GitBranch, Route, Maximize2 } from 'lucide-react';
import axiosInstance from '../../utils/axiosConfig';
import TaskSlideOver from './TaskSlideOver';
import EmptyState from './EmptyState';
import TaskBar, { COLOR_CRITICO } from './gantt/TaskBar';
import GanttHeader from './gantt/GanttHeader';
import GanttGrid from './gantt/GanttGrid';
import GroupRangeBar from './gantt/GroupRangeBar';
import HitosLane, { HitosLaneSidebar, HitosOverlay } from './gantt/HitosLane';
import useCamaraGantt from './gantt/useCamaraGantt';
import usePaneo from './gantt/usePaneo';
import ControlZoom from './gantt/ControlZoom';
import { PriorityIcon, HITO_COLOR_DEFAULT } from './utils/displays';
import {
  ZOOM, ZOOM_ORDEN, ROW_HEIGHT, SIDEBAR_WIDTH,
  addDays, diffDays, ventanaGantt, xDias, anchoDias,
} from './gantt/constants';
import { CATEGORIAS, CATEGORIA_COLOR, PROYECTO_COLOR_DEFAULT } from './utils/constants';
import { refTareaUrl, tareaPorRefUrl } from './utils/helpers';

const DIA_MS = 86400000;

// Preferencias de la vista que sobreviven a recargas (por navegador). Todo
// en try/catch: en modo privado localStorage puede fallar. El Gantt de varios
// proyectos guarda las suyas aparte: agrupa por proyecto por defecto y no
// debe pisar la agrupacion elegida dentro de un proyecto.
const prefKey = (varios) => (varios ? 'pm.gantt.prefs.general' : 'pm.gantt.prefs');
const leerPrefs = (varios) => {
  try { return JSON.parse(window.localStorage.getItem(prefKey(varios)) || '{}') || {}; } catch { return {}; }
};
const guardarPrefs = (varios, prefs) => {
  try { window.localStorage.setItem(prefKey(varios), JSON.stringify(prefs)); } catch { /* sin storage */ }
};

const AGRUPACIONES = [
  { key: 'estado', label: 'Estado' },
  { key: 'hito', label: 'Hito' },
  { key: 'asignado', label: 'Persona' },
];

// Con tareas de varios proyectos se puede agrupar por proyecto (y es lo
// primero que se ve: una barra por proyecto con sus hitos).
const AGRUPACIONES_VARIOS = [{ key: 'proyecto', label: 'Proyecto' }, ...AGRUPACIONES];

const escaparRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Aire al final de la lista: la ultima tarea no queda pegada al borde.
const ESPACIO_FINAL = 56;

const inicioDeTarea = (t) => t.fecha_inicio || t.fecha_vencimiento || '9999-12-31';
const ordenarPorInicio = (lista) => [...lista].sort((a, b) => (
  inicioDeTarea(a).localeCompare(inicioDeTarea(b)) || (a.numero || 0) - (b.numero || 0)
));

const fechaCorta = (iso) => (iso
  ? new Date(iso + 'T00:00:00').toLocaleDateString('es-VE', { day: 'numeric', month: 'short' })
  : '—');

/**
 * El Gantt del modulo. Uno solo para todo: el de cada proyecto, el de la vista
 * general y el Gantt general (/proyectos/gantt) son este mismo componente con
 * otro contexto de datos.
 *
 * - Escalas Día / Semana / Mes / Trimestre con encabezado de dos niveles, y
 *   zoom de camara entre escalas (ver useCamaraGantt).
 * - Ventana calculada desde las fechas de las tareas, hitos y el proyecto
 *   (no un rango fijo alrededor de hoy). "Ajustar" elige el zoom que hace
 *   caber todo el proyecto en pantalla.
 * - Agrupa por estado, hito o persona, con barra resumen por grupo; con varios
 *   proyectos, tambien por proyecto (con sus hitos sobre la barra).
 * - Flechas de dependencias visibles por defecto; "Ruta crítica" (solo dentro
 *   de un proyecto) resalta en naranja las tareas y flechas de la cadena que
 *   define el fin del proyecto (calculada en el backend, ver ruta_critica_service.py).
 *
 * contexto: mismo contrato que el Outlet context de un proyecto.
 * rutaBase: ruta del Gantt; el slide-over vive en `${rutaBase}/tareas/:ref`.
 */
export const GanttVista = ({ contexto, rutaBase }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    proyecto, tareas, refetchTareas, updateTareaLocal,
    // Vista general: tareas de todos los proyectos (hitos ya vienen unificados).
    esVistaGeneral = false, proyectosMap,
  } = contexto;

  const agrupaciones = esVistaGeneral ? AGRUPACIONES_VARIOS : AGRUPACIONES;
  const prefsIniciales = useMemo(() => leerPrefs(esVistaGeneral), [esVistaGeneral]);
  const [agrupar, setAgrupar] = useState(
    agrupaciones.some((a) => a.key === prefsIniciales.agrupar)
      ? prefsIniciales.agrupar
      : agrupaciones[0].key
  );
  const [mostrarDeps, setMostrarDeps] = useState(prefsIniciales.deps !== false);
  const [verRutaCritica, setVerRutaCritica] = useState(!!prefsIniciales.rutaCritica && !esVistaGeneral);
  const [collapsed, setCollapsed] = useState({});
  const [sinProgramarOpen, setSinProgramarOpen] = useState(false);
  const [slideOverTareaId, setSlideOverTareaId] = useState(null);
  const [rutaData, setRutaData] = useState(null);

  const scrollRef = useRef(null);
  const sidebarRef = useRef(null);
  // Lienzo de la linea de tiempo: la camara le escribe --dw (ancho del dia).
  const lienzoRef = useRef(null);

  // Hitos del proyecto con fecha (vienen del detalle, via Outlet context).
  const hitos = useMemo(
    () => (proyecto?.hitos || []).filter((h) => h && h.fecha),
    [proyecto]
  );

  // Ventana visible desde el contenido, para cualquier escala.
  const ventanaPara = useCallback((z) => ventanaGantt({
    tareas: tareas || [],
    hitos,
    extras: [proyecto?.fecha_inicio, proyecto?.fecha_fin].filter(Boolean),
    zoom: z,
  }), [tareas, hitos, proyecto?.fecha_inicio, proyecto?.fecha_fin]);

  // Zoom y scroll horizontal: la camara (ver useCamaraGantt).
  const camara = useCamaraGantt({
    zoomInicial: ZOOM[prefsIniciales.zoom] ? prefsIniciales.zoom : 'semana',
    ventanaPara,
    scrollRef,
    lienzoRef,
  });
  const { zoom, dayWidth, dayWidthTextos } = camara;

  // La mano: arrastrar el fondo recorre la linea de tiempo.
  const paneo = usePaneo(scrollRef);

  // Alto de la barra de scroll horizontal del Gantt: el lateral necesita ese
  // mismo extra abajo para que las filas sigan alineadas al llegar al final.
  const [barraScroll, setBarraScroll] = useState(0);
  useLayoutEffect(() => {
    const medir = () => {
      const el = scrollRef.current;
      if (el) setBarraScroll(Math.max(0, el.offsetHeight - el.clientHeight));
    };
    medir();
    window.addEventListener('resize', medir);
    return () => window.removeEventListener('resize', medir);
  }, []);
  const { firstDay, totalDays, todayOffset } = camara.ventana;

  // La vista general no tiene ruta crítica: no pisar la preferencia del
  // proyecto con su `false`.
  useEffect(() => {
    guardarPrefs(esVistaGeneral, {
      ...leerPrefs(esVistaGeneral),
      zoom,
      agrupar,
      deps: mostrarDeps,
      ...(esVistaGeneral ? {} : { rutaCritica: verRutaCritica }),
    });
  }, [zoom, agrupar, mostrarDeps, verRutaCritica, esVistaGeneral]);

  // ----- Ruta crítica (solo proyecto real) -----

  const puedeRutaCritica = !esVistaGeneral && !!proyecto?.id;
  useEffect(() => {
    if (!verRutaCritica || !puedeRutaCritica) { setRutaData(null); return undefined; }
    let vivo = true;
    // Pequeño debounce: al arrastrar barras las tareas cambian seguido.
    const timer = setTimeout(async () => {
      try {
        const { data } = await axiosInstance.get(`/api/proyectos/${proyecto.id}/ruta-critica`);
        if (vivo) setRutaData(data);
      } catch (err) {
        console.error('Error cargando ruta crítica:', err);
      }
    }, 300);
    return () => { vivo = false; clearTimeout(timer); };
  }, [verRutaCritica, puedeRutaCritica, proyecto?.id, tareas]);

  const criticas = useMemo(
    () => new Set(verRutaCritica && rutaData ? rutaData.ruta_critica : []),
    [verRutaCritica, rutaData]
  );

  // ----- Grupos -----

  const conFecha = useMemo(
    () => (tareas || []).filter((t) => t.fecha_inicio || t.fecha_vencimiento),
    [tareas]
  );

  const sinProgramar = useMemo(
    () => (tareas || []).filter((t) => !t.fecha_inicio && !t.fecha_vencimiento),
    [tareas]
  );

  const grupos = useMemo(() => {
    if (agrupar === 'proyecto') {
      // Una fila por proyecto (barra con el color del proyecto y sus hitos
      // encima), plegada de entrada: primero se ve el portafolio completo.
      const porProyecto = new Map();
      conFecha.forEach((t) => {
        const pid = t.proyecto_id;
        if (!porProyecto.has(pid)) {
          const p = proyectosMap?.[pid];
          porProyecto.set(pid, {
            key: `p-${pid}`,
            label: p?.nombre || t.proyecto_nombre || 'Proyecto',
            color: p?.color || t.proyecto_color || PROYECTO_COLOR_DEFAULT,
            hitos: (p?.hitos || []).filter((h) => h && h.fecha),
            destacado: true,
            plegadoInicial: true,
            tareas: [],
          });
        }
        porProyecto.get(pid).tareas.push(t);
      });
      return Array.from(porProyecto.values())
        .sort((a, b) => a.label.localeCompare(b.label, 'es'))
        .map((g) => ({ ...g, tareas: ordenarPorInicio(g.tareas) }));
    }

    if (agrupar === 'hito') {
      const porHito = new Map();
      hitos.forEach((h) => porHito.set(h.id, { hito: h, tareas: [] }));
      const sinHito = [];
      conFecha.forEach((t) => {
        const hid = t.hito_id ?? t.hito?.id;
        if (hid == null) { sinHito.push(t); return; }
        if (!porHito.has(hid)) porHito.set(hid, { hito: t.hito || { id: hid, nombre: 'Hito', fecha: null }, tareas: [] });
        porHito.get(hid).tareas.push(t);
      });
      const lista = Array.from(porHito.values())
        .sort((a, b) => (a.hito.fecha || '9999').localeCompare(b.hito.fecha || '9999'))
        .map(({ hito, tareas: ts }) => ({
          key: `h-${hito.id}`,
          label: hito.nombre,
          color: hito.color || HITO_COLOR_DEFAULT,
          tareas: ordenarPorInicio(ts),
        }));
      lista.push({ key: 'h-none', label: 'Sin hito', color: '#94a3b8', tareas: ordenarPorInicio(sinHito) });
      return lista.filter((g) => g.tareas.length > 0);
    }

    if (agrupar === 'asignado') {
      const porPersona = new Map();
      const sinAsignar = [];
      conFecha.forEach((t) => {
        if (!t.asignados?.length) { sinAsignar.push(t); return; }
        t.asignados.forEach((a) => {
          if (!porPersona.has(a.id)) porPersona.set(a.id, { persona: a, tareas: [] });
          porPersona.get(a.id).tareas.push(t);
        });
      });
      const lista = Array.from(porPersona.values())
        .sort((a, b) => (a.persona.nombre || '').localeCompare(b.persona.nombre || ''))
        .map(({ persona, tareas: ts }) => ({
          key: `a-${persona.id}`,
          label: persona.nombre,
          color: '#0891b2',
          tareas: ordenarPorInicio(ts),
        }));
      lista.push({ key: 'a-none', label: 'Sin asignar', color: '#94a3b8', tareas: ordenarPorInicio(sinAsignar) });
      return lista.filter((g) => g.tareas.length > 0);
    }

    const map = {};
    CATEGORIAS.forEach((c) => { map[c.key] = []; });
    conFecha.forEach((t) => {
      const cat = t.estado?.categoria;
      if (cat && map[cat]) map[cat].push(t);
    });
    return CATEGORIAS
      .map((c) => ({ key: c.key, label: c.label, color: CATEGORIA_COLOR[c.key], tareas: ordenarPorInicio(map[c.key]) }))
      .filter((g) => g.tareas.length > 0);
  }, [agrupar, conFecha, hitos, proyectosMap]);

  const totalConFecha = conFecha.length;

  // Plegado de un grupo: lo que eligio el usuario o, si no toco nada, el de
  // entrada del grupo (los proyectos arrancan plegados).
  const estaPlegado = useCallback(
    (g) => collapsed[g.key] ?? !!g.plegadoInicial,
    [collapsed]
  );

  // Agrupado por proyecto, los hitos van sobre la barra de cada proyecto; la
  // lane de hitos repetiria los mismos rombos.
  const mostrarLaneHitos = hitos.length > 0 && agrupar !== 'proyecto';

  // ----- Scroll y camara -----

  const { registrarScroll, irA } = camara;

  const onScrollGantt = (e) => {
    const el = e.currentTarget;
    registrarScroll(el);
    const sb = sidebarRef.current;
    if (sb && Math.abs(sb.scrollTop - el.scrollTop) > 1) sb.scrollTop = el.scrollTop;
  };

  const onScrollSidebar = (e) => {
    const g = scrollRef.current;
    if (g && Math.abs(g.scrollTop - e.currentTarget.scrollTop) > 1) g.scrollTop = e.currentTarget.scrollTop;
  };

  // Ajustar: el zoom más detallado en el que todo el contenido (tareas e
  // hitos, sin contar hoy) cabe en el ancho visible. La camara viaja a esa
  // escala y deja el inicio del contenido cerca del borde izquierdo.
  const ajustarAlProyecto = useCallback(() => {
    const el = scrollRef.current;
    const fechas = [];
    (tareas || []).forEach((t) => {
      if (t.fecha_inicio) fechas.push(t.fecha_inicio);
      if (t.fecha_vencimiento) fechas.push(t.fecha_vencimiento);
    });
    hitos.forEach((h) => fechas.push(h.fecha));
    if (!el || fechas.length === 0) return;
    fechas.sort();
    const min = new Date(fechas[0] + 'T00:00:00');
    const max = new Date(fechas[fechas.length - 1] + 'T00:00:00');
    const span = diffDays(max, min) + 1;
    const disponible = el.clientWidth - 96;
    const elegido = [...ZOOM_ORDEN].reverse().find((z) => span * ZOOM[z].px <= disponible) || 'trimestre';
    const centro = min.getTime() + ((el.clientWidth / 2 - 48) / ZOOM[elegido].px) * DIA_MS;
    irA(elegido, centro);
  }, [tareas, hitos, irA]);

  // ----- Slide-over -----

  useEffect(() => {
    const match = location.pathname.match(new RegExp(`^${escaparRegExp(rutaBase)}/tareas/(\\d+)`));
    if (match) {
      const ref = parseInt(match[1], 10);
      const t = tareaPorRefUrl(tareas, ref, esVistaGeneral);
      if (t) setSlideOverTareaId(t.id);
    } else {
      setSlideOverTareaId(null);
    }
  }, [tareas, location.pathname, esVistaGeneral, rutaBase]);

  const handleCardClick = useCallback((tarea) => {
    setSlideOverTareaId(tarea.id);
    navigate(`${rutaBase}/tareas/${refTareaUrl(tarea, esVistaGeneral)}`, { replace: false });
  }, [navigate, rutaBase, esVistaGeneral]);

  const handleCloseSlideOver = useCallback(() => {
    setSlideOverTareaId(null);
    navigate(rutaBase, { replace: false });
  }, [navigate, rutaBase]);

  // Update optimista al mover/resize barra: aplica patch local + PUT. Si falla, refetch.
  const handleTareaUpdate = useCallback(async (tareaId, patch) => {
    updateTareaLocal?.(tareaId, patch);
    try {
      const { data } = await axiosInstance.put(`/api/tareas/${tareaId}`, patch);
      updateTareaLocal?.(tareaId, data);
    } catch (err) {
      console.error('Error actualizando gantt:', err);
      refetchTareas?.();
    }
  }, [updateTareaLocal, refetchTareas]);

  const ganttWidth = totalDays * dayWidth;

  // Posición de cada tarea visible (para anclar flechas). Mismo orden que el
  // DOM: lane de hitos + sin fecha (+ items si abierto) + por grupo (header +
  // tareas si no está colapsado). Con agrupación por persona una tarea puede
  // salir en varios grupos: las flechas se anclan a su primera aparición.
  const necesitaPosiciones = mostrarDeps || verRutaCritica;
  const { taskPositions, totalRows } = useMemo(() => {
    const map = new Map();
    let row = 0;
    if (mostrarLaneHitos) row += 1;
    if (sinProgramar.length > 0) {
      row += 1;
      if (sinProgramarOpen) row += sinProgramar.length;
    }
    grupos.forEach((g) => {
      row += 1;
      if (!estaPlegado(g)) {
        g.tareas.forEach((t) => {
          if (necesitaPosiciones && !map.has(t.id)) {
            const fi = t.fecha_inicio ? new Date(t.fecha_inicio + 'T00:00:00') : null;
            const ff = t.fecha_vencimiento ? new Date(t.fecha_vencimiento + 'T00:00:00') : null;
            const start = fi || ff;
            const end = ff || fi;
            const offsetDays = diffDays(start, firstDay);
            const duration = Math.max(1, diffDays(end, start) + 1);
            map.set(t.id, {
              xStart: offsetDays * dayWidth,
              xEnd: offsetDays * dayWidth + Math.max(8, duration * dayWidth - 4),
              yCenter: row * ROW_HEIGHT + ROW_HEIGHT / 2,
            });
          }
          row += 1;
        });
      }
    });
    return { taskPositions: map, totalRows: row };
  }, [necesitaPosiciones, mostrarLaneHitos, sinProgramar, sinProgramarOpen, grupos, estaPlegado, firstDay, dayWidth]);

  // Flechas: una por cada par predecesora -> sucesora con ambas visibles.
  // Crítica (ambas en la ruta, con el resaltado activo) > en riesgo > normal.
  const depLines = useMemo(() => {
    if (!necesitaPosiciones) return [];
    const lineas = [];
    const hoy = new Date(); hoy.setHours(0, 0, 0, 0);
    (tareas || []).forEach((t) => {
      const pos = taskPositions.get(t.id);
      if (!pos) return;
      (t.bloqueada_por || []).forEach((dep) => {
        const predPos = taskPositions.get(dep.id);
        if (!predPos) return;
        const critica = criticas.has(t.id) && criticas.has(dep.id);
        if (!mostrarDeps && !critica) return;
        const predHecha = dep.estado_categoria === 'hecho' || dep.estado_categoria === 'cancelado';
        const sucesoraProxima = t.fecha_vencimiento
          && new Date(t.fecha_vencimiento + 'T00:00:00').getTime() <= hoy.getTime() + 2 * DIA_MS;
        lineas.push({
          id: `dep-${dep.dep_id ?? `${dep.id}-${t.id}`}`,
          x1: predPos.xEnd,
          y1: predPos.yCenter,
          x2: pos.xStart,
          y2: pos.yCenter,
          critica,
          enRiesgo: !predHecha && sucesoraProxima,
        });
      });
    });
    // Las críticas al final para que queden por encima.
    return lineas.sort((a, b) => Number(a.critica) - Number(b.critica));
  }, [necesitaPosiciones, mostrarDeps, tareas, taskPositions, criticas]);

  const totalDeps = useMemo(
    () => (tareas || []).reduce((n, t) => n + (t.bloqueada_por?.length || 0), 0),
    [tareas]
  );

  // El slide-over necesita la config del proyecto REAL de la tarea; en la vista
  // general el del contexto es sintetico (union de todos).
  const proyectoDelSlideOver = useMemo(() => {
    if (!esVistaGeneral || !slideOverTareaId) return proyecto;
    const t = (tareas || []).find((x) => x.id === slideOverTareaId);
    return (t && proyectosMap?.[t.proyecto_id]) || proyecto;
  }, [esVistaGeneral, slideOverTareaId, tareas, proyectosMap, proyecto]);

  const resumenRuta = verRutaCritica ? rutaData?.resumen : null;
  const hayContenido = grupos.length > 0 || sinProgramar.length > 0;

  return (
    <div className="relative h-full flex flex-col bg-white">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-y-2 px-6 pt-4 pb-3 border-b border-slate-200/60 flex-shrink-0">
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={camara.irAHoy}
            className="px-2 py-1 text-xs text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded transition-colors"
          >
            Hoy
          </button>
          <button
            onClick={ajustarAlProyecto}
            title="Elegir el zoom en el que se ve todo el proyecto"
            className="inline-flex items-center gap-1 px-2 py-1 text-xs text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded transition-colors"
          >
            <Maximize2 size={11} strokeWidth={2} />
            Ajustar
          </button>

          <div className="h-4 w-px bg-slate-200 mx-1" />

          <ControlZoom zoom={zoom} orden={ZOOM_ORDEN} escalas={ZOOM} onCambiar={camara.cambiarZoom} />

          <div className="h-4 w-px bg-slate-200 mx-1" />

          <span className="text-xs text-slate-400">Agrupar</span>
          <Segmentado
            opciones={agrupaciones}
            valor={agrupar}
            onChange={setAgrupar}
            ariaLabel="Agrupar por"
          />

          <div className="h-4 w-px bg-slate-200 mx-1" />

          <button
            onClick={() => setMostrarDeps((v) => !v)}
            aria-pressed={mostrarDeps}
            title={mostrarDeps ? 'Ocultar flechas de dependencias' : 'Mostrar flechas de dependencias'}
            className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs rounded transition-colors ${
              mostrarDeps
                ? 'bg-slate-900 text-white hover:bg-slate-800'
                : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'
            }`}
          >
            <GitBranch size={11} strokeWidth={2} />
            Dependencias
            {totalDeps > 0 && <span className={`tabular-nums ${mostrarDeps ? 'text-slate-300' : 'text-slate-400'}`}>{totalDeps}</span>}
          </button>

          {puedeRutaCritica && (
            <button
              onClick={() => setVerRutaCritica((v) => !v)}
              aria-pressed={verRutaCritica}
              title="Resaltar la cadena de tareas que define la fecha de fin del proyecto"
              className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs rounded transition-colors ${
                verRutaCritica
                  ? 'text-white hover:opacity-90'
                  : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'
              }`}
              style={verRutaCritica ? { backgroundColor: COLOR_CRITICO } : undefined}
            >
              <Route size={11} strokeWidth={2} />
              Ruta crítica
            </button>
          )}
        </div>

        {resumenRuta ? (
          <Link
            to={`/proyectos/${proyecto?.slug}/red`}
            className="text-xs text-slate-500 hover:text-slate-800 tabular-nums transition-colors"
            title="Ver la red completa y el análisis"
          >
            <span className="font-semibold" style={{ color: COLOR_CRITICO }}>{resumenRuta.criticas} críticas</span>
            <span className="mx-1.5 text-slate-300">/</span>
            desviación {resumenRuta.desviacion_pct}%
            <span className="mx-1.5 text-slate-300">/</span>
            fin proyectado {fechaCorta(resumenRuta.fin_proyectado)}
          </Link>
        ) : (
          <span className="text-xs text-slate-400 tabular-nums">
            {totalConFecha} con fecha, {sinProgramar.length} sin fecha
          </span>
        )}
      </div>

      {/* Body */}
      <div className="flex-1 flex overflow-hidden min-h-0">
        {/* Sidebar */}
        <div className="flex flex-col flex-shrink-0 border-r border-slate-200/60 bg-white" style={{ width: SIDEBAR_WIDTH }}>
          <div className="border-b border-slate-200/60 bg-slate-50/40" style={{ height: 65 }}>
            <div className="flex items-center h-full px-4">
              <span className="text-xs font-medium text-slate-500">Tarea</span>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto" ref={sidebarRef} onScroll={onScrollSidebar}>
            {mostrarLaneHitos && (
              <HitosLaneSidebar count={hitos.length} height={ROW_HEIGHT} />
            )}

            {sinProgramar.length > 0 && (
              <SinProgramarSidebar
                items={sinProgramar}
                onClick={handleCardClick}
                open={sinProgramarOpen}
                onToggle={() => setSinProgramarOpen((v) => !v)}
              />
            )}

            {grupos.map((g) => {
              const isCol = estaPlegado(g);
              return (
                <div key={g.key}>
                  <button
                    onClick={() => setCollapsed((c) => ({ ...c, [g.key]: !isCol }))}
                    className={`w-full flex items-center gap-2 px-4 py-2 transition-colors group ${
                      g.destacado
                        ? 'bg-slate-100/70 hover:bg-slate-100 border-y border-slate-200/70'
                        : 'bg-slate-50/40 hover:bg-slate-100/60'
                    }`}
                    style={{ height: ROW_HEIGHT }}
                  >
                    <motion.span
                      animate={{ rotate: isCol ? 0 : 90 }}
                      transition={{ duration: 0.15 }}
                      className="text-slate-400 group-hover:text-slate-600"
                    >
                      <ChevronRight size={11} strokeWidth={2.5} />
                    </motion.span>
                    <span
                      className={`${g.destacado ? 'w-2.5 h-2.5' : 'w-1.5 h-1.5'} rounded-full flex-shrink-0`}
                      style={{ backgroundColor: g.color }}
                    />
                    <span className={g.destacado
                      ? 'text-sm font-bold tracking-tight text-slate-900 truncate'
                      : 'text-xs font-semibold text-slate-700 truncate'}
                    >
                      {g.label}
                    </span>
                    <span className="text-[10px] font-medium text-slate-400 tabular-nums ml-auto">{g.tareas.length}</span>
                  </button>
                  {!isCol && g.tareas.map((t) => {
                    const esCritica = criticas.has(t.id);
                    return (
                      <button
                        key={`${g.key}-${t.id}`}
                        onClick={() => handleCardClick(t)}
                        className="relative w-full flex items-center gap-2 px-4 pl-8 text-left hover:bg-slate-50 transition-colors"
                        style={{ height: ROW_HEIGHT }}
                      >
                        {esCritica && (
                          <span
                            className="absolute left-4 top-2 bottom-2 w-[3px] rounded-full"
                            style={{ backgroundColor: COLOR_CRITICO }}
                            title="En la ruta crítica"
                          />
                        )}
                        <span className="flex items-center justify-center w-3 flex-shrink-0">
                          <PriorityIcon prioridad={t.prioridad} compact />
                        </span>
                        <span className="text-xs text-slate-800 truncate">{t.titulo}</span>
                      </button>
                    );
                  })}
                </div>
              );
            })}

            {hayContenido && <div aria-hidden style={{ height: ESPACIO_FINAL + barraScroll }} />}

            {!hayContenido && (
              <EmptyState
                icon={Inbox}
                title={esVistaGeneral ? 'Todavía no hay tareas.' : 'Este proyecto aún no tiene tareas.'}
                variant="card"
              />
            )}
          </div>
        </div>

        {/* Gantt */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-auto cursor-grab"
          onScroll={onScrollGantt}
          onMouseDown={paneo.onMouseDown}
          onWheel={paneo.onWheel}
        >
          <div ref={lienzoRef} className="relative" style={{ width: anchoDias(totalDays) }}>
            <GanttHeader
              firstDay={firstDay}
              totalDays={totalDays}
              zoom={zoom}
              capas={camara.capas}
              rango={camara.rango}
            />

            <div className="relative" style={{ minHeight: totalRows * ROW_HEIGHT + ESPACIO_FINAL }}>
              <GanttGrid
                firstDay={firstDay}
                totalDays={totalDays}
                zoom={zoom}
                capas={camara.capas}
                rango={camara.rango}
              />

              {/* Hoy */}
              {todayOffset >= 0 && todayOffset < totalDays && (
                <div
                  className="absolute top-0 bottom-0 w-px bg-rose-500/70 pointer-events-none z-10"
                  style={{ left: xDias(todayOffset + 0.5) }}
                />
              )}

              {/* Flechas de dependencia — codos en SVG. pointer-events none
                  para no interferir con el drag/click de las barras. Se
                  calculan en px, asi que no viajan con la camara: se apagan
                  durante el zoom y reaparecen con un fundido al llegar. */}
              {depLines.length > 0 && !camara.viajando && (
                <motion.svg
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.25 }}
                  className="absolute top-0 left-0 pointer-events-none"
                  width={ganttWidth}
                  height={totalRows * ROW_HEIGHT}
                  style={{ zIndex: 6 }}
                >
                  <defs>
                    <marker id="dep-arrow-slate" viewBox="0 0 8 8" refX="6" refY="4" markerWidth="6" markerHeight="6" orient="auto">
                      <path d="M 0 0 L 8 4 L 0 8 z" fill="#94a3b8" />
                    </marker>
                    <marker id="dep-arrow-rose" viewBox="0 0 8 8" refX="6" refY="4" markerWidth="6" markerHeight="6" orient="auto">
                      <path d="M 0 0 L 8 4 L 0 8 z" fill="#f43f5e" />
                    </marker>
                    <marker id="dep-arrow-critico" viewBox="0 0 8 8" refX="6" refY="4" markerWidth="6" markerHeight="6" orient="auto">
                      <path d="M 0 0 L 8 4 L 0 8 z" fill={COLOR_CRITICO} />
                    </marker>
                  </defs>
                  {depLines.map((l) => {
                    // Codo: sale del extremo derecho de la predecesora, baja/sube
                    // a la fila de la sucesora y entra por su borde izquierdo. Si
                    // la sucesora empieza antes de que termine la predecesora
                    // (solapadas), el codo rodea por la izquierda.
                    const gap = 8;
                    let path;
                    if (l.x2 - gap >= l.x1 + gap) {
                      const cx = l.x1 + gap;
                      path = `M ${l.x1} ${l.y1} L ${cx} ${l.y1} L ${cx} ${l.y2} L ${l.x2 - 4} ${l.y2}`;
                    } else {
                      const midY = l.y1 + (l.y2 > l.y1 ? ROW_HEIGHT / 2 : -ROW_HEIGHT / 2);
                      path = `M ${l.x1} ${l.y1} L ${l.x1 + gap} ${l.y1} L ${l.x1 + gap} ${midY} L ${l.x2 - gap} ${midY} L ${l.x2 - gap} ${l.y2} L ${l.x2 - 4} ${l.y2}`;
                    }
                    let stroke = '#94a3b8';
                    let marker = 'url(#dep-arrow-slate)';
                    let width = 1.25;
                    let dash = '3 2';
                    if (l.critica) {
                      stroke = COLOR_CRITICO; marker = 'url(#dep-arrow-critico)'; width = 2; dash = '0';
                    } else if (l.enRiesgo) {
                      stroke = '#f43f5e'; marker = 'url(#dep-arrow-rose)'; width = 1.75; dash = '0';
                    }
                    return (
                      <path
                        key={l.id}
                        d={path}
                        fill="none"
                        stroke={stroke}
                        strokeWidth={width}
                        strokeDasharray={dash}
                        markerEnd={marker}
                      />
                    );
                  })}
                </motion.svg>
              )}

              {/* Lane de hitos (sincronizada con HitosLaneSidebar) */}
              {mostrarLaneHitos && (
                <HitosLane
                  hitos={hitos}
                  firstDay={firstDay}
                  height={ROW_HEIGHT}
                />
              )}

              {/* Sin fecha: 1 fila header + N tareas si abierto. Click en
                  cualquier día de la fila asigna fecha_vencimiento. */}
              {sinProgramar.length > 0 && (
                <>
                  <div className="border-b border-slate-100 bg-amber-50/20" style={{ height: ROW_HEIGHT }} />
                  {sinProgramarOpen && sinProgramar.map((t) => (
                    <ClickToScheduleRow
                      key={`sp-${t.id}`}
                      tarea={t}
                      firstDay={firstDay}
                      dayWidth={dayWidth}
                      onSchedule={(fechaISO) => handleTareaUpdate(t.id, { fecha_vencimiento: fechaISO })}
                    />
                  ))}
                </>
              )}

              {/* Grupos con barra resumen + barras de tareas */}
              {grupos.map((g) => {
                const isCol = estaPlegado(g);
                return (
                  <div key={g.key}>
                    <div
                      className={`relative ${g.destacado
                        ? 'border-y border-slate-200/70 bg-slate-100/70'
                        : 'border-b border-slate-100 bg-slate-50/30'}`}
                      style={{ height: ROW_HEIGHT }}
                    >
                      <GroupRangeBar tareas={g.tareas} firstDay={firstDay} color={g.color} />
                      {g.hitos?.length > 0 && (
                        <HitosOverlay
                          hitos={g.hitos}
                          firstDay={firstDay}
                          cy={ROW_HEIGHT / 2}
                          size={11}
                          enLienzo
                        />
                      )}
                    </div>
                    {!isCol && g.tareas.map((t) => (
                      <div
                        key={`${g.key}-${t.id}`}
                        className="relative border-b border-slate-100 hover:bg-slate-50/40"
                        style={{ height: ROW_HEIGHT }}
                      >
                        <TaskBar
                          tarea={t}
                          dayWidth={dayWidthTextos}
                          firstDay={firstDay}
                          onClick={handleCardClick}
                          onUpdate={handleTareaUpdate}
                          critica={criticas.has(t.id)}
                        />
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Empty state full-vista (sin tareas ni hitos). */}
      {!hayContenido && hitos.length === 0 && (
        <div className="absolute inset-x-0 bottom-0 top-[105px] pointer-events-none">
          <EmptyState
            icon={GanttChartSquare}
            title="Sin nada que graficar"
            description={esVistaGeneral
              ? 'Cuando tus proyectos tengan tareas con fechas, van a aparecer acá en el Gantt.'
              : 'Cuando el proyecto tenga tareas con fechas, van a aparecer acá en el Gantt.'}
            className="pointer-events-auto"
          />
        </div>
      )}

      <TaskSlideOver
        tareaId={slideOverTareaId}
        proyecto={proyectoDelSlideOver}
        onClose={handleCloseSlideOver}
        onUpdate={(tareaCompleta) => updateTareaLocal?.(tareaCompleta.id, tareaCompleta)}
        onArchive={() => { refetchTareas?.(); handleCloseSlideOver(); }}
        onDuplicate={(nueva) => { refetchTareas?.(); setSlideOverTareaId(nueva.id); }}
      />
    </div>
  );
};

// ---------- Control segmentado (agrupar) ----------
//
// La pastilla blanca se desliza a la opcion elegida (layoutId por control).

const Segmentado = ({ opciones, valor, onChange, ariaLabel }) => (
  <div role="radiogroup" aria-label={ariaLabel} className="inline-flex items-center p-0.5 rounded-md bg-slate-100">
    {opciones.map((o) => {
      const activo = o.key === valor;
      return (
        <button
          key={o.key}
          role="radio"
          aria-checked={activo}
          onClick={() => onChange(o.key)}
          className={`relative px-2 py-0.5 text-xs rounded transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500/40 ${
            activo ? 'text-slate-900 font-medium' : 'text-slate-500 hover:text-slate-800'
          }`}
        >
          {activo && (
            <motion.span
              layoutId={`segmentado-${ariaLabel}`}
              transition={{ type: 'spring', stiffness: 520, damping: 38 }}
              className="absolute inset-0 rounded bg-white shadow-sm"
            />
          )}
          <span className="relative">{o.label}</span>
        </button>
      );
    })}
  </div>
);

// ---------- Sin programar (sidebar) ----------

const SinProgramarSidebar = ({ items, onClick, open, onToggle }) => {
  return (
    <div>
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-2 px-4 py-2 bg-amber-50/30 hover:bg-amber-50/60 transition-colors group"
        style={{ height: ROW_HEIGHT }}
      >
        <motion.span
          animate={{ rotate: open ? 90 : 0 }}
          transition={{ duration: 0.15 }}
          className="text-slate-400 group-hover:text-slate-600"
        >
          <ChevronRight size={11} strokeWidth={2.5} />
        </motion.span>
        <Inbox size={11} className="text-amber-600" strokeWidth={2} />
        <span className="text-[11px] font-semibold tracking-tight text-amber-700">Sin fecha</span>
        <span className="text-[10px] font-medium text-amber-500/70 tabular-nums ml-auto">{items.length}</span>
      </button>
      {open && items.map((t) => (
        <button
          key={t.id}
          onClick={() => onClick(t)}
          className="w-full flex items-center gap-2 px-4 pl-8 text-left hover:bg-slate-50 transition-colors"
          style={{ height: ROW_HEIGHT }}
        >
          <span className="flex items-center justify-center w-3 flex-shrink-0">
            <PriorityIcon prioridad={t.prioridad} compact />
          </span>
          <span className="text-xs text-slate-800 truncate">{t.titulo}</span>
        </button>
      ))}
    </div>
  );
};

// ---------- Click-to-schedule (tareas sin fecha) ----------
//
// Fila vacia clickeable: al click en cualquier dia, asigna fecha_vencimiento.
// Preview con linea vertical al hover indica que dia se va a fijar.

const _toISO = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};

const ClickToScheduleRow = ({ tarea, firstDay, dayWidth, onSchedule }) => {
  const [hoverX, setHoverX] = useState(null);

  const onMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const dia = Math.floor(x / dayWidth);
    setHoverX(dia * dayWidth);
  };

  const onLeave = () => setHoverX(null);

  const onClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const diaOffset = Math.floor(x / dayWidth);
    const fecha = addDays(firstDay, diaOffset);
    onSchedule(_toISO(fecha));
  };

  return (
    <div
      className="relative border-b border-slate-100 cursor-pointer hover:bg-cyan-50/30 transition-colors group"
      style={{ height: ROW_HEIGHT }}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      onClick={onClick}
      title={`Click en cualquier dia para asignar fecha de vencimiento a "${tarea.titulo}"`}
    >
      {hoverX !== null && (
        <>
          {/* Linea vertical preview */}
          <div
            className="absolute top-0 bottom-0 w-px bg-cyan-500/60 pointer-events-none"
            style={{ left: hoverX + dayWidth / 2 }}
          />
          {/* Badge con etiqueta del dia */}
          <div
            className="absolute top-1/2 -translate-y-1/2 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide bg-cyan-500 text-white rounded pointer-events-none"
            style={{ left: hoverX + dayWidth / 2 + 6 }}
          >
            Fijar acá
          </div>
        </>
      )}
    </div>
  );
};

// ---------- Ruta /proyectos/:slug/gantt ----------
//
// El Gantt de un proyecto (o de la vista general): GanttVista con el contexto
// del proyecto. El Gantt general del modulo es este mismo con todos los
// proyectos (ver GanttGlobalView).

const ProyectoGanttView = () => {
  const { slug } = useParams();
  const contexto = useOutletContext();
  return <GanttVista contexto={contexto} rutaBase={`/proyectos/${slug}/gantt`} />;
};

export default ProyectoGanttView;
